// Pestaña Perfil / KYC: la identidad, las cinco verificaciones y el origen del cliente. Sale de
// `GET /customers/{id}/summary360`, más las sesiones del wizard, el expediente y el crédito.
import type { ReactNode } from 'react';
import { ErrorState } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateMx } from '@/lib/dateHelpers';
import {
    customerFullName,
    type RetailCustomerSummary,
} from '@/api/services/retail-customer/retail-customer.client';
import type { CustomerKycSession } from '@/api/services/retail-wizard/customerKyc.client';
import type { CustomerCreditDetail } from '@/domains/sales/portal/customers/api/useCustomerCredit';
import type { CreditCustomerRow } from '../lib/creditCustomersFilters';
import {
    originStoreFailureGap,
    summaryErrorCopy,
    summaryFieldFailureGap,
} from '../lib/creditCustomersErrors';
import { formatPhone, NO_PHONE, UNNAMED_CUSTOMER } from '../lib/creditCustomersFormat';
import {
    ageOf,
    documentTypeLabel,
    INVALID_BIRTH_DATE,
    KYC_STATUS_CHIP_CLASS,
    KYC_STATUS_LABEL,
    KYC_STATUS_NOTE,
    NO_BIRTH_DATE,
    NO_KYC_STATUS,
    RFC_BASE_NOTE,
    rfcBaseOf,
} from '../lib/customer360';
import {
    DOCUMENTS_WITHOUT_BODY,
    KYC_SESSIONS_NO_SCOPE,
    ORIGIN_STORE_NO_SCOPE,
    NO_CREATED_AT,
    NO_CREDIT_TO_AUTHORIZE,
    NO_CURP,
    NO_ID_DOCUMENT,
    NO_KYC_SESSIONS,
    NO_ORIGIN_STORE,
    NO_ORIGIN_STORE_NAME,
    NO_RFC_BASE,
} from '../lib/customer360Gaps';
import {
    bureauAuthorizationVerdict,
    creditFailureGap,
    idDocumentVerdict,
    KYC_CHIP_TONE_CLASS,
    KYC_OUTCOME_CHIP_CLASS,
    KYC_OUTCOME_LABEL,
    KYC_OUTCOME_NOTE,
    kycSessionsFailureGap,
    livenessOutcomeOf,
    proofOfAddressChip,
    type KycChipVerdict,
} from '../lib/customerKycChecks';
import { documentsFailureGap, onboardingBlockGap } from '../lib/customerDocuments';
import type { CustomerSummaryState, OriginStoreState } from '../api/useCustomer360';
import type { CustomerDocumentsState } from '../api/useCustomerDocuments';
import type { CustomerKycSessionsState } from '../api/useCustomerKycSessions';
import { CustomerNotesCard } from './CustomerNotesCard';
import {
    CHIP,
    CopyButton,
    Field,
    FileCard,
    LoadingValue,
    NoSourceValue,
    ReadGap,
    UnreadValue,
} from './CustomerFileUi';

const SUMMARY_SUBJECT = 'la ficha de este cliente';

const SESSIONS_SUBJECT = 'las verificaciones de este cliente';

const DOCUMENTS_SUBJECT = 'el expediente de este cliente';

const CREDIT_SUBJECT = 'el crédito de este cliente';

export function CustomerProfileTab({
    row,
    summary,
    originStore,
    kycSessions,
    documents,
    credit,
    now,
}: {
    row: CreditCustomerRow;
    summary: CustomerSummaryState;
    originStore: OriginStoreState;
    /** Las verificaciones de MetaMap: otra lectura más, contra el silo del wizard. */
    kycSessions: CustomerKycSessionsState;
    /** El expediente de onboarding: de ahí sale qué lados de la identificación llegaron. */
    documents: CustomerDocumentsState;
    /** El crédito: su contrato firmado es lo que asienta la autorización de buró. */
    credit: CustomerCreditDetail;
    /** El instante contra el que se calcula la edad. */
    now: Date;
}) {
    const customer = summary.data?.customer;

    return (
        <>
            <IdentityCard row={row} customer={customer} summary={summary} now={now} />
            {summary.failure ? (
                <SummaryFailure summary={summary} />
            ) : (
                <>
                    <KycVerificationCard
                        customer={customer}
                        isLoading={summary.isLoading}
                        kycSessions={kycSessions}
                        documents={documents}
                        credit={credit}
                    />
                    <OriginCard
                        customer={customer}
                        isLoading={summary.isLoading}
                        originStore={originStore}
                    />
                    <CustomerNotesCard notes={customer?.notes} isLoading={summary.isLoading} />
                </>
            )}
        </>
    );
}

/** El desenlace de la ficha 360 cuando no se pudo leer. Solo la caída ofrece reintentar. */
function SummaryFailure({ summary }: { summary: CustomerSummaryState }) {
    const copy = summaryErrorCopy(summary.failure ?? 'error');
    return (
        <ErrorState
            title={copy.title}
            description={copy.description}
            onRetry={copy.canRetry ? summary.retry : undefined}
        />
    );
}

/**
 * Identidad. Sobrevive al 403 de la ficha 360: el nombre y el teléfono ya vinieron con el padrón, y
 * borrarlos porque falló OTRA lectura dejaría la columna sin decir de quién es.
 */
function IdentityCard({
    row,
    customer,
    summary,
    now,
}: {
    row: CreditCustomerRow;
    customer: RetailCustomerSummary | undefined;
    summary: CustomerSummaryState;
    now: Date;
}) {
    const name = customer ? customerFullName(customer) : (row.fullName ?? UNNAMED_CUSTOMER);
    const phone = formatPhone(customer?.phone ?? row.phone ?? '');
    const rfcBase = rfcBaseOf(customer?.curp);
    const age = ageOf(customer?.birthDate, now);

    return (
        <FileCard title="Identidad">
            <Field label="Nombre completo">{name}</Field>
            <Field label="CURP">
                <SummaryValue summary={summary}>
                    {customer?.curp ? (
                        <>
                            <span className="font-mono">{customer.curp}</span>
                            <CopyButton value={customer.curp} subject="CURP" />
                        </>
                    ) : (
                        <ReadGap gap={NO_CURP} />
                    )}
                </SummaryValue>
            </Field>
            <Field label="RFC (base)">
                <SummaryValue summary={summary}>
                    {rfcBase ? (
                        <>
                            <span title={RFC_BASE_NOTE} className="cursor-help font-mono">
                                {rfcBase}
                            </span>
                            <CopyButton value={rfcBase} subject="RFC base" />
                        </>
                    ) : (
                        <ReadGap gap={NO_RFC_BASE} />
                    )}
                </SummaryValue>
            </Field>
            <Field label="Edad">
                <SummaryValue summary={summary}>
                    {age.state === 'known' ? (
                        `${String(age.years)} años`
                    ) : age.state === 'no-birth-date' ? (
                        <ReadGap gap={NO_BIRTH_DATE} />
                    ) : (
                        <ReadGap gap={INVALID_BIRTH_DATE} />
                    )}
                </SummaryValue>
            </Field>
            <Field label="Teléfono">
                {phone ? (
                    <>
                        <span className="font-mono">{phone}</span>
                        <CopyButton value={phone} subject="teléfono" />
                    </>
                ) : (
                    <ReadGap gap={NO_PHONE} />
                )}
            </Field>
        </FileCard>
    );
}

/**
 * Las cinco verificaciones del mock. Cada una sale de OTRA lectura y se cae por su cuenta: el chip
 * dice el estado de la suya, no el de la tarjeta entera.
 */
function KycVerificationCard({
    customer,
    isLoading,
    kycSessions,
    documents,
    credit,
}: {
    customer: RetailCustomerSummary | undefined;
    isLoading: boolean;
    kycSessions: CustomerKycSessionsState;
    documents: CustomerDocumentsState;
    credit: CustomerCreditDetail;
}) {
    const kycStatus = customer?.kycStatus ?? null;

    return (
        <FileCard title="KYC · verificación">
            <Field label="Antifraude MetaMap">
                <Loaded isLoading={isLoading}>
                    {kycStatus ? (
                        <span
                            title={KYC_STATUS_NOTE[kycStatus]}
                            className={cn(CHIP, 'cursor-help', KYC_STATUS_CHIP_CLASS[kycStatus])}
                        >
                            {KYC_STATUS_LABEL[kycStatus]}
                        </span>
                    ) : (
                        <NoSourceValue gap={NO_KYC_STATUS} />
                    )}
                </Loaded>
            </Field>
            {/* El rótulo es el del mock; el tipo real va en el `title` porque el flujo también
                acepta pasaporte, licencia y documento extranjero. */}
            <Field label="INE / IFE" title={documentTypeLabel(customer?.documentType) ?? undefined}>
                <IdDocumentValue documents={documents} />
            </Field>
            <Field label="Comprobante domicilio">
                <SessionValue sessions={kycSessions}>
                    {(session) => <ProofOfAddressValue session={session} />}
                </SessionValue>
            </Field>
            <Field label="Prueba de vida (liveness)">
                <SessionValue sessions={kycSessions}>
                    {(session) => <LivenessValue session={session} />}
                </SessionValue>
            </Field>
            <Field label="Autorización de buró">
                <BureauAuthorizationValue credit={credit} />
            </Field>
        </FileCard>
    );
}

/** Un chip derivado, con el motivo del que sale en el `title`. */
function ChipVerdict({ verdict }: { verdict: KycChipVerdict }) {
    return (
        <span
            title={verdict.note}
            className={cn(CHIP, 'cursor-help', KYC_CHIP_TONE_CLASS[verdict.tone])}
        >
            {verdict.label}
        </span>
    );
}

/** Qué lados de la identificación tiene el expediente, o por qué no se sabe. */
function IdDocumentValue({ documents }: { documents: CustomerDocumentsState }) {
    if (documents.isLoading) return <LoadingValue subject={DOCUMENTS_SUBJECT} />;
    if (documents.failure) return <UnreadValue gap={documentsFailureGap(documents.failure)} />;

    const data = documents.data;
    if (!data) return <UnreadValue gap={DOCUMENTS_WITHOUT_BODY} />;

    const verdict = idDocumentVerdict(data.onboarding);
    if (verdict) return <ChipVerdict verdict={verdict} />;
    // Sin ningún lado, el motivo lo da el bloque: sabe si el expediente está vacío o si no se leyó.
    return <ReadGap gap={onboardingBlockGap(data.blocks.onboarding) ?? NO_ID_DOCUMENT} />;
}

function ProofOfAddressValue({ session }: { session: CustomerKycSession }) {
    return <ChipVerdict verdict={proofOfAddressChip(session.proofOfAddress)} />;
}

function LivenessValue({ session }: { session: CustomerKycSession }) {
    const outcome = livenessOutcomeOf(session);

    return (
        <span
            title={KYC_OUTCOME_NOTE[outcome]}
            className={cn(CHIP, 'cursor-help', KYC_OUTCOME_CHIP_CLASS[outcome])}
        >
            {KYC_OUTCOME_LABEL[outcome]}
        </span>
    );
}

/** La firma del contrato del crédito es la que asienta la autorización de consulta al SIC. */
function BureauAuthorizationValue({ credit }: { credit: CustomerCreditDetail }) {
    if (credit.state === 'loading') return <LoadingValue subject={CREDIT_SUBJECT} />;
    if (credit.state !== 'ready') return <UnreadValue gap={creditFailureGap(credit.state)} />;

    const verdict = bureauAuthorizationVerdict(credit.data.credits);
    if (!verdict) return <ReadGap gap={NO_CREDIT_TO_AUTHORIZE} />;
    return <ChipVerdict verdict={verdict} />;
}

/** Dónde y cuándo entró el cliente al padrón. La tienda es una lectura aparte, con otro permiso. */
function OriginCard({
    customer,
    isLoading,
    originStore,
}: {
    customer: RetailCustomerSummary | undefined;
    isLoading: boolean;
    originStore: OriginStoreState;
}) {
    return (
        <FileCard title="Origen">
            <Field label="Tienda de alta">
                <Loaded isLoading={isLoading}>
                    <OriginStoreValue customer={customer} originStore={originStore} />
                </Loaded>
            </Field>
            <Field label="Fecha alta en SK">
                <Loaded isLoading={isLoading}>
                    {customer?.createdAt ? (
                        formatDateMx(customer.createdAt)
                    ) : (
                        <ReadGap gap={NO_CREATED_AT} />
                    )}
                </Loaded>
            </Field>
        </FileCard>
    );
}

/**
 * El nombre de la tienda, o el motivo por el que no se pudo resolver. El id crudo se leía como un
 * dato real y dejaba al operador sin saber que ahí faltaba un nombre.
 */
function OriginStoreValue({
    customer,
    originStore,
}: {
    customer: RetailCustomerSummary | undefined;
    originStore: OriginStoreState;
}) {
    if (!customer?.originStoreId) return <ReadGap gap={NO_ORIGIN_STORE} />;
    if (originStore.name) return <span>{originStore.name}</span>;
    if (originStore.hasNoScope) return <UnreadValue gap={ORIGIN_STORE_NO_SCOPE} />;
    if (originStore.isLoading) return <LoadingValue subject="la tienda de alta" />;
    if (originStore.failure)
        return <UnreadValue gap={originStoreFailureGap(originStore.failure)} />;
    return <ReadGap gap={NO_ORIGIN_STORE_NAME} />;
}

/** Envuelve un valor de la ficha 360: mientras la lectura vuela, no se afirma que el dato falta. */
function Loaded({ isLoading, children }: { isLoading: boolean; children: ReactNode }) {
    if (isLoading) return <LoadingValue subject={SUMMARY_SUBJECT} />;
    return <>{children}</>;
}

/**
 * Igual que `Loaded`, pero para la identidad, que se pinta TAMBIÉN cuando la ficha 360 falló. Con
 * la lectura caída el campo dice que no se leyó: sus `children` afirmarían un hueco del alta.
 */
function SummaryValue({
    summary,
    children,
}: {
    summary: CustomerSummaryState;
    children: ReactNode;
}) {
    if (summary.isLoading) return <LoadingValue subject={SUMMARY_SUBJECT} />;
    if (summary.failure) return <UnreadValue gap={summaryFieldFailureGap(summary.failure)} />;
    return <>{children}</>;
}

/** Los dos campos que salen de las sesiones del wizard dicen, cada uno, en qué quedó ESA lectura. */
function SessionValue({
    sessions,
    children,
}: {
    sessions: CustomerKycSessionsState;
    children: (session: CustomerKycSession) => ReactNode;
}) {
    if (sessions.hasNoScope) return <UnreadValue gap={KYC_SESSIONS_NO_SCOPE} />;
    if (sessions.isLoading) return <LoadingValue subject={SESSIONS_SUBJECT} />;
    if (sessions.failure) return <UnreadValue gap={kycSessionsFailureGap(sessions.failure)} />;
    if (!sessions.latest) return <ReadGap gap={NO_KYC_SESSIONS} />;
    return <>{children(sessions.latest)}</>;
}
